/**
 * POLAR drafting-aid side effects extracted from `Scene3DStage.tsx` (#492 @codex
 * P2), keeping that component under its 2000-line cap. Two effects that mirror
 * the OSNAP + ORTHO refresh effects that stay inline in `Scene3DStage`:
 *
 *   1. POLAR-STALE-CACHE — re-resolve the snap at the LAST cursor position when
 *      the effective polar configuration changes WITHOUT a pointer move (the F10 /
 *      POLARMODE toggle or a held-Shift temp override via `selectEffectivePolar`,
 *      the `POLARANG` increment, the POLARADDANG additional-angle list + its
 *      "use additional angles" bit, POLARMODE's relative-basis bit
 *      (`selectPolarAngleBasis`, N6), or TRACKPATH's polar-path-visible bit
 *      (`selectPolarTrackPathVisible`, N6)). `resolveAndCacheSnap` reads all of
 *      these at event time through `polarLockedCursor`, so without a refresh a
 *      click after the change would commit `lastSnappedWorld` constrained under
 *      the OLD polar config and leave the dashed guide / tooltip stale. The
 *      pre-existing OSNAP + ORTHO refresh effects do not cover polar. Keyed on
 *      primitives (+ a joined key for the angle array) so each genuine change
 *      fires once; the re-resolve mutates none of them → no loop.
 *      `refreshSnapAtLastPointer` no-ops off-canvas / pre-first-move. The
 *      reference-angle-itself change (a new point committed under relative
 *      basis) is a SEPARATE re-key that stays in `Scene3DStage.tsx` alongside
 *      `orthoAnchorKey` (`polarReferenceAngleKey`) — this hook only owns the
 *      polar CONFIG subscriptions, not the per-render anchor/reference refs.
 *
 *   2. POLAR-DRAFT-END-CLEAR — the polar guide + tooltip follow the live draft,
 *      but the only clears live in the pointer move / leave paths. When a draft
 *      SESSION ends WITHOUT another pointer move — a pick that completes/cancels
 *      the command (e.g. finishing a CIRCLE radius pick), Escape, or a command
 *      abort — the draft preview is removed yet the dashed guide / tip would
 *      linger over the canvas until the cursor next moves. Clear them the instant
 *      no draft is active. Guarded so it only writes on a real transition.
 */

import { useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from "react";

import {
  useSysvarStore,
  selectEffectivePolar,
  selectPolarAddAngEnabled,
  selectPolarAngleBasis,
  selectPolarTrackPathVisible,
} from "../../dynamicInput/sysvarStore";
import { usePolarTrackingPrefs } from "../../state/polarTrackingPrefs";
import type { OverlayState } from "../overlays";

interface UsePolarDraftAidEffectsArgs {
  /** Carrier for `installStageInteractions().refreshSnapAtLastPointer`. */
  refreshSnapAtLastPointerRef: MutableRefObject<(() => void) | null>;
  /** Overlay state setter (to clear the polar guide / tip on draft end). */
  setOverlayState: Dispatch<SetStateAction<OverlayState>>;
  /** Whether a draft command is currently active (`draftSession != null`). */
  isDrafting: boolean;
}

export function usePolarDraftAidEffects({
  refreshSnapAtLastPointerRef,
  setOverlayState,
  isDrafting,
}: UsePolarDraftAidEffectsArgs): void {
  const effectivePolar = useSysvarStore(selectEffectivePolar);
  const polarAng = useSysvarStore((s) => s.values.POLARANG);
  // POLARMODE bit 4 (POLARADDANG) is now the single source of the "use additional
  // angles" flag (#492 P2) — subscribe to it so a DSETTINGS/SETVAR toggle refreshes.
  const polarUseAdditional = useSysvarStore(selectPolarAddAngEnabled);
  const polarAddAnglesKey = usePolarTrackingPrefs((s) =>
    s.additionalAnglesDeg.join(","),
  );
  // N6 (POLAR-RELATIVE-BASIS-WIRE) — the angle-measurement basis (POLARMODE's
  // relative-basis bit) and TRACKPATH's polar-path visibility are BOTH read
  // live by `polarLockedCursor`, so a DSETTINGS/SETVAR edit to either — with
  // no pointer move — must re-resolve the same way a POLARANG edit already
  // does, or the cached guide/tooltip (or a click committed without moving)
  // would reflect the OLD basis / display setting.
  const polarAngleBasis = useSysvarStore(selectPolarAngleBasis);
  const polarTrackPathVisible = useSysvarStore(selectPolarTrackPathVisible);
  useEffect(() => {
    refreshSnapAtLastPointerRef.current?.();
  }, [
    effectivePolar,
    polarAng,
    polarUseAdditional,
    polarAddAnglesKey,
    polarAngleBasis,
    polarTrackPathVisible,
    refreshSnapAtLastPointerRef,
  ]);

  useEffect(() => {
    if (isDrafting) return;
    setOverlayState((prev) =>
      prev.polarGuide == null && prev.polarTip == null
        ? prev
        : { ...prev, polarGuide: null, polarTip: null },
    );
  }, [isDrafting, setOverlayState]);
}
